import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import SendDeliveryDialog from "./SendDeliveryDialog";
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  Send,
  Loader2,
  RefreshCw,
  X,
  Plus,
  FileSignature,
  ExternalLink,
  Link2,
  PhoneCall,
} from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * VEXOS Tracker — a 4-stage delivery pipeline that lives inside a project.
 * Andre clicks through Kickoff/Intake → V1 → V2 → Final Delivery. Only at
 * Final Delivery does the "Send Go-Live Sign-Off" DocuSign action unlock.
 */
export default function VexosTrackerTab({ programId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [showGoLive, setShowGoLive] = useState(false);
  const [sendRound, setSendRound] = useState(null);
  const [savingOps, setSavingOps] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await apiClient.get(`/programs/${programId}/vexos-tracker`);
      setData(res);
      return res;
    } catch (e) {
      toast.error("Couldn't load the VEXOS tracker");
      return null;
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const deliveryRounds = data?.delivery?.rounds || [];
  const roundForStage = (stage) => deliveryRounds.find((r) => r.stage === stage) || null;

  const setStage = async (stage) => {
    if (!data?.can_manage || advancing) return;
    setAdvancing(true);
    try {
      await apiClient.put(`/programs/${programId}/vexos-tracker`, { stage });
      toast.success(`Moved to “${stage}”`);
      const fresh = await load();
      // Moving onto a delivery stage IS the moment the client should hear about
      // it, so the send dialog follows the click rather than waiting to be
      // found. Cancelling leaves the stage moved — that's what clicking a stage
      // has always meant, and the client simply hasn't been told yet.
      const round = (fresh?.delivery?.rounds || []).find((r) => r.stage === stage);
      if (round) setSendRound(round);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the stage");
    } finally {
      setAdvancing(false);
    }
  };

  // Board-side VEXOS state (Management View card): client outreach + closing.
  const updateOps = async (changes, okMessage) => {
    setSavingOps(true);
    try {
      const { data: res } = await apiClient.patch(`/management/vexos-ops/${programId}`, changes);
      setData((d) => ({ ...d, ops: res.vexos_ops || {} }));
      toast.success(okMessage);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally {
      setSavingOps(false);
    }
  };

  const sendToClient = async ({ url, note, recipients, to, cc, save_to_team }) => {
    try {
      const { data: res } = await apiClient.post(
        `/project-tracker/program/${programId}/vexos-rounds/${sendRound.key}/send`,
        { url, note, recipients, to, cc, save_to_team, advance_stage: true },
      );
      setSendRound(null);
      await load();
      const sent = res?.send?.reached ?? res?.send?.sent ?? 0;
      if (sent > 0) {
        toast.success(`${sendRound.label} sent to ${sent} recipient${sent === 1 ? "" : "s"}.`);
      } else if (res?.send?.reason) {
        toast.warning(`Link saved, but nothing was emailed — ${res.send.reason}.`);
      } else {
        toast.warning("Link saved, but no email left — check the send mode in Tracker admin.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that delivery");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading VEXOS tracker…
      </div>
    );
  }
  if (!data) return null;

  const stages = data.stages || [];
  const currentIdx = stages.indexOf(data.stage);
  const golive = data.golive;
  const goliveCompleted = golive?.docusign_status === "completed" || golive?.completed_at;
  const ops = data.ops || {};
  const contactedOn = ops.client_contacted_at
    ? new Date(ops.client_contacted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="space-y-6">
      <Card className="border border-border">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>VEXOS Tracker</h3>
              <p className="text-sm text-muted-foreground">
                Track platform delivery. The Go-Live Sign-Off unlocks at Final Delivery.
              </p>
            </div>
            {data.updated_by && (
              <span className="text-xs text-muted-foreground hidden sm:block">
                Updated by {data.updated_by}
              </span>
            )}
          </div>

          {/* Client outreach — the first thing the VEXOS manager does, and what
              the VEXOS card on the Management View shows at a glance. */}
          <div
            className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border p-3 mb-5 ${
              contactedOn ? "bg-emerald-500/10 border-emerald-500/30" : "bg-orange-100 border-orange-200"
            }`}
            data-testid="vexos-client-outreach"
          >
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              {contactedOn ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <PhoneCall className="w-5 h-5 text-orange-700 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className={`text-sm font-medium ${contactedOn ? "text-emerald-800" : "text-orange-700"}`}>
                  {contactedOn ? "Client contacted" : "Client not contacted yet"}
                </p>
                <p className={`text-xs ${contactedOn ? "text-emerald-700" : "text-orange-700"}`}>
                  {contactedOn
                    ? `Marked ${contactedOn}${ops.client_contacted_by ? ` by ${ops.client_contacted_by}` : ""}.`
                    : "Mark this once you've reached out to the client about their VEXOS platform."}
                </p>
              </div>
            </div>
            {data.can_manage && (contactedOn ? (
              <Button variant="ghost" size="sm" className="rounded-full text-xs h-8 shrink-0"
                disabled={savingOps}
                onClick={() => updateOps({ client_contacted: false }, "Client contact un-marked")}>
                Undo
              </Button>
            ) : (
              <Button size="sm" className="rounded-full text-xs h-8 shrink-0"
                disabled={savingOps}
                onClick={() => updateOps({ client_contacted: true }, "Marked: client contacted")}
                data-testid="mark-client-contacted">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark client contacted
              </Button>
            ))}
          </div>

          {/* Stepper */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1">
            {stages.map((s, i) => {
              const done = i < currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s} className="flex items-center gap-2 sm:flex-1">
                  <button
                    disabled={!data.can_manage || advancing}
                    onClick={() => setStage(s)}
                    data-testid={`vexos-stage-${i}`}
                    className={`flex items-center gap-2 w-full rounded-full px-3 py-2 text-sm font-medium border transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : done
                        ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                        : "bg-muted/40 text-muted-foreground border-border hover:border-muted-foreground/40"
                    } ${data.can_manage ? "cursor-pointer" : "cursor-default"}`}
                  >
                    {done ? (
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                    ) : active ? (
                      <FileSignature className="w-4 h-4 shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 shrink-0" />
                    )}
                    <span className="truncate">{s}</span>
                  </button>
                  {i < stages.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 hidden sm:block" />
                  )}
                </div>
              );
            })}
          </div>

          {!data.can_manage && (
            <p className="text-xs text-muted-foreground mt-4">
              Only the VEXOS manager can advance the tracker or send the sign-off.
            </p>
          )}

          {ops.closed_at && (
            <p className="text-xs text-muted-foreground mt-4">
              This project's VEXOS card is off the Management View.
              {data.can_manage && (
                <button className="ml-1 underline hover:text-foreground" disabled={savingOps}
                  onClick={() => updateOps({ closed: false }, "VEXOS card is back on the board")}>
                  Put it back
                </button>
              )}
            </p>
          )}

          {/* What the client has actually been sent.
              The stage pointer above says where the platform IS; this says what
              the client has been TOLD, and those two drifting apart is the gap
              this block exists to close. */}
          {deliveryRounds.length > 0 && (
            <div className="mt-6 pt-5 border-t border-border">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="font-medium text-sm">Client deliveries</p>
                  <p className="text-xs text-muted-foreground">
                    Paste the demo link and we email the client a button into their dashboard.
                  </p>
                </div>
                {data.delivery?.email_mode && data.delivery.email_mode !== "live" && (
                  <span className="text-[11px] rounded-full border border-amber-300 bg-amber-50 text-amber-800 px-2.5 py-1 shrink-0">
                    Delivery email {data.delivery.email_mode}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {deliveryRounds.map((r) => (
                  <div key={r.key} className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{r.label} · {r.title}</p>
                      {r.url ? (
                        <a href={r.url} target="_blank" rel="noreferrer"
                          className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1 truncate max-w-full">
                          <Link2 className="w-3 h-3 shrink-0" />
                          <span className="truncate">{r.url}</span>
                        </a>
                      ) : (
                        <p className="text-xs text-muted-foreground">No link sent yet</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.sent_text && (
                        <span className="text-xs text-muted-foreground">Sent {r.sent_text}</span>
                      )}
                      {data.can_manage && (
                        <Button size="sm" variant={r.sent_at ? "outline" : "default"}
                          className="rounded-full text-xs h-8"
                          onClick={() => setSendRound(r)}>
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                          {r.sent_at ? "Resend" : "Send to client"}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {data.delivery && !data.delivery.recipients?.length && (
                <p className="text-xs text-amber-700 mt-3">
                  This project has no client email on its tracker yet — add one on the Client Tracker tab
                  before sending a delivery.
                </p>
              )}
            </div>
          )}

          {/* Go-Live action / status */}
          <div className="mt-6 pt-5 border-t border-border">
            {goliveCompleted ? (
              <div className="flex items-start gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-800">Go-Live Sign-Off completed</p>
                  <p className="text-sm text-emerald-700/80">
                    Signed by {golive?.signer_email}. The platform is approved to go live.
                  </p>
                </div>
              </div>
            ) : golive?.docusign_envelope_id ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <p className="font-medium">Go-Live Sign-Off sent</p>
                  <p className="text-sm text-muted-foreground">
                    Awaiting signature from {golive?.signer_email}
                    {golive?.cc_emails?.length ? ` (+${golive.cc_emails.length} cc)` : ""}.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="golive-refresh">
                    <RefreshCw className="w-4 h-4 mr-2" /> Check status
                  </Button>
                  {data.can_manage && (
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShowGoLive(true)}>
                      Resend
                    </Button>
                  )}
                </div>
              </div>
            ) : data.at_final_delivery ? (
              data.can_manage ? (
                <Button
                  className="rounded-full"
                  onClick={() => setShowGoLive(true)}
                  data-testid="send-golive-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Send Go-Live Sign-Off
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">Ready for the VEXOS manager to send the Go-Live Sign-Off.</p>
              )
            ) : (
              <p className="text-sm text-muted-foreground">
                Advance to <span className="font-medium text-foreground">Final Delivery</span> to send the Go-Live Sign-Off.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {sendRound && (
        <SendDeliveryDialog
          open
          onClose={() => setSendRound(null)}
          onSend={sendToClient}
          roundLabel={sendRound.label}
          roundTitle={sendRound.title}
          linkLabel="VEXOS demo link"
          linkPlaceholder="https://vexos…"
          linkHint="The live platform link the client should walk through."
          initialUrl={sendRound.url || ""}
          recipients={data.delivery?.recipients || []}
          emailMode={data.delivery?.email_mode}
          alreadySentText={sendRound.sent_text}
          programId={programId}
          pickSessions={false}
          onRecipientsChange={() => load()}
        />
      )}

      {showGoLive && (
        <GoLiveDialog
          programId={programId}
          context={data.context || {}}
          existing={golive}
          onClose={() => setShowGoLive(false)}
          onSent={async () => { setShowGoLive(false); await load(); }}
        />
      )}
    </div>
  );
}

function GoLiveDialog({ programId, context, existing, onClose, onSent }) {
  const [platformUrl, setPlatformUrl] = useState(existing?.platform_url || "");
  const [clientEmail, setClientEmail] = useState(existing?.signer_email || context.client_email || "");
  const [clientEntity, setClientEntity] = useState(context.client_entity || context.client_name || "");
  const [vizPm, setVizPm] = useState(context.viz_pm || "");
  const [ccEmails, setCcEmails] = useState(existing?.cc_emails?.length ? existing.cc_emails : []);
  const [ccInput, setCcInput] = useState("");
  const [sending, setSending] = useState(false);

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const addCc = () => {
    const e = ccInput.trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { toast.error("That doesn't look like a valid email"); return; }
    if (e === clientEmail.trim().toLowerCase() || ccEmails.includes(e)) { setCcInput(""); return; }
    setCcEmails([...ccEmails, e]);
    setCcInput("");
  };

  const send = async () => {
    if (!EMAIL_RE.test(clientEmail.trim())) { toast.error("Enter a valid client email"); return; }
    if (!platformUrl.trim()) { toast.error("Platform URL is required"); return; }
    setSending(true);
    try {
      await apiClient.post(`/programs/${programId}/golive/send`, {
        client_email: clientEmail.trim(),
        platform_url: platformUrl.trim(),
        viz_pm: vizPm.trim() || null,
        client_entity: clientEntity.trim() || null,
        cc_emails: ccEmails,
      });
      toast.success("Go-Live Sign-Off sent to the client");
      await onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the sign-off");
    } finally {
      setSending(false);
    }
  };

  const ReadOnly = ({ label, value }) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <p className="text-sm font-medium mt-1">{value || "—"}</p>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Send VEXOS Go-Live Sign-Off</DialogTitle>
          <DialogDescription>
            Confirm the details below. The client signs in DocuSign; you'll get an email when it's completed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/40 p-3">
            <ReadOnly label="Client" value={context.client_name} />
            <ReadOnly label="Project" value={context.project_name} />
            <ReadOnly label="Delivery date" value={today} />
            <ReadOnly label="VEXOS Manager" value={context.vexos_manager} />
          </div>

          <div>
            <Label htmlFor="golive-entity">Client legal entity (LLC)</Label>
            <Input id="golive-entity" value={clientEntity} onChange={(e) => setClientEntity(e.target.value)}
              placeholder="e.g. Park Tower LLC" className="mt-1" />
          </div>

          <div>
            <Label htmlFor="golive-vizpm">Viz PM</Label>
            <Input id="golive-vizpm" value={vizPm} onChange={(e) => setVizPm(e.target.value)}
              placeholder="Viz PM name" className="mt-1" />
          </div>

          <div>
            <Label htmlFor="golive-url">Platform URL <span className="text-destructive">*</span></Label>
            <Input id="golive-url" value={platformUrl} onChange={(e) => setPlatformUrl(e.target.value)}
              placeholder="https://…" className="mt-1" data-testid="golive-platform-url" />
          </div>

          <div>
            <Label htmlFor="golive-email">Client email (signer) <span className="text-destructive">*</span></Label>
            <Input id="golive-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}
              placeholder="client@company.com" className="mt-1" data-testid="golive-client-email" />
          </div>

          <div>
            <Label>Also send to (cc)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={ccInput} onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
                placeholder="another@company.com" type="email" />
              <Button type="button" variant="outline" onClick={addCc} className="shrink-0">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {ccEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {ccEmails.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs">
                    {e}
                    <button onClick={() => setCcEmails(ccEmails.filter((x) => x !== e))} className="text-muted-foreground hover:text-foreground">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button className="rounded-full" onClick={send} disabled={sending} data-testid="golive-send-confirm">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send to client</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
